import type { Job, JobWorker, RealtimeEvent, RealtimeSnapshot, Worker } from '../types'

/**
 * Normalized entity state. Reducers are pure and return the *same* object references for
 * anything that did not change, so memoized rows/markers skip re-rendering.
 */
export interface EntitiesState {
  workersById: Record<string, Worker>
  workerIds: string[]
  jobsById: Record<string, Job>
  jobIds: string[]
}

export const emptyEntities: EntitiesState = {
  workersById: {},
  workerIds: [],
  jobsById: {},
  jobIds: [],
}

const byStart = (jobsById: Record<string, Job>) => (a: string, b: string) => {
  const aStart = Date.parse(jobsById[a]?.scheduledStart ?? '') || 0
  const bStart = Date.parse(jobsById[b]?.scheduledStart ?? '') || 0
  return aStart - bStart || a.localeCompare(b)
}

export function hydrateEntities(snapshot: Pick<RealtimeSnapshot, 'workers' | 'jobs'>): EntitiesState {
  const workersById: Record<string, Worker> = {}
  const workerIds: string[] = []
  snapshot.workers.forEach((worker) => {
    if (workersById[worker.id]) return
    workersById[worker.id] = worker
    workerIds.push(worker.id)
  })

  const jobsById: Record<string, Job> = {}
  const jobIds: string[] = []
  snapshot.jobs.forEach((job) => {
    if (jobsById[job.id]) return
    jobsById[job.id] = job
    jobIds.push(job.id)
  })
  jobIds.sort(byStart(jobsById))

  return { workersById, workerIds, jobsById, jobIds }
}

function shallowEqual<T extends object>(a: T, b: T): boolean {
  const aKeys = Object.keys(a) as Array<keyof T>
  const bKeys = Object.keys(b) as Array<keyof T>
  return aKeys.length === bKeys.length && aKeys.every((key) => Object.is(a[key], b[key]))
}

/** Jobs are equal when their fields match and each JobWorker matches field-by-field. */
export function jobsEqual(a: Job, b: Job): boolean {
  if (a === b) return true
  const { workers: aWorkers, ...aRest } = a
  const { workers: bWorkers, ...bRest } = b
  if (!shallowEqual(aRest, bRest)) return false
  if (aWorkers.length !== bWorkers.length) return false
  return aWorkers.every((worker, index) => shallowEqual(worker, bWorkers[index]))
}

function patchWorker(state: EntitiesState, id: string, patch: Partial<Worker>): EntitiesState {
  const current = state.workersById[id]
  if (!current) return state
  const next = { ...current, ...patch }
  if (shallowEqual(current, next)) return state
  return { ...state, workersById: { ...state.workersById, [id]: next } }
}

function upsertJob(state: EntitiesState, job: Job): EntitiesState {
  const current = state.jobsById[job.id]
  if (current && jobsEqual(current, job)) return state
  const jobsById = { ...state.jobsById, [job.id]: job }
  const startChanged = !current || current.scheduledStart !== job.scheduledStart
  const jobIds = current ? state.jobIds : [...state.jobIds, job.id]
  return {
    ...state,
    jobsById,
    jobIds: startChanged ? [...jobIds].sort(byStart(jobsById)) : jobIds,
  }
}

function removeJob(state: EntitiesState, jobId: string): EntitiesState {
  if (!state.jobsById[jobId]) return state
  const jobsById = { ...state.jobsById }
  delete jobsById[jobId]
  return { ...state, jobsById, jobIds: state.jobIds.filter((id) => id !== jobId) }
}

/** Job status follows staffing: no active workers -> unassigned; first worker -> scheduled. */
function withStaffingStatus(job: Job): Job {
  const hasActive = job.workers.some((worker) => worker.response === 'pending' || worker.response === 'accepted')
  if (!hasActive && job.status !== 'completed' && job.status !== 'in_progress') return job.status === 'unassigned' ? job : { ...job, status: 'unassigned' }
  if (hasActive && job.status === 'unassigned') return { ...job, status: 'scheduled' }
  return job
}

function patchJobWorker(state: EntitiesState, jobId: string, workerId: string, patch: Partial<JobWorker>): EntitiesState {
  const job = state.jobsById[jobId]
  if (!job) return state
  const index = job.workers.findIndex((worker) => worker.workerId === workerId)
  if (index === -1) return state
  const current = job.workers[index]
  const next = { ...current, ...patch }
  if (shallowEqual(current, next)) return state
  const workers = [...job.workers]
  workers[index] = next
  return upsertJob(state, withStaffingStatus({ ...job, workers }))
}

function addJobWorker(state: EntitiesState, jobId: string, jobWorker: JobWorker): EntitiesState {
  const job = state.jobsById[jobId]
  if (!job) return state
  // Duplicates are ignored (server echo of an optimistic add, or a double click).
  if (job.workers.some((worker) => worker.workerId === jobWorker.workerId)) {
    return patchJobWorker(state, jobId, jobWorker.workerId, jobWorker)
  }
  return upsertJob(state, withStaffingStatus({ ...job, workers: [...job.workers, jobWorker] }))
}

function removeJobWorker(state: EntitiesState, jobId: string, workerId: string): EntitiesState {
  const job = state.jobsById[jobId]
  if (!job || !job.workers.some((worker) => worker.workerId === workerId)) return state
  return upsertJob(state, withStaffingStatus({ ...job, workers: job.workers.filter((worker) => worker.workerId !== workerId) }))
}

export function applyRealtimeEvent(state: EntitiesState, event: RealtimeEvent): EntitiesState {
  switch (event.type) {
    case 'worker.location.updated':
    case 'worker.location':
      return patchWorker(state, event.workerId, { lat: event.lat, lng: event.lng, lastSeenAt: event.at, locationSource: 'live' })

    case 'worker.status.changed':
    case 'worker.status':
      return patchWorker(state, event.workerId, {
        status: event.status,
        lastSeenAt: event.at,
        ...(event.currentJobId !== undefined ? { currentJobId: event.currentJobId } : {}),
      })

    case 'job.created':
      return upsertJob(state, event.job)

    case 'job.updated': {
      const current = state.jobsById[event.job.id]
      if (!current) return state
      return upsertJob(state, { ...current, ...event.job })
    }

    case 'job.deleted':
      return removeJob(state, event.jobId)

    case 'job.assigned':
      if (!state.workersById[event.workerId]) return state
      return addJobWorker(state, event.jobId, { workerId: event.workerId, response: 'pending', status: 'assigned', sentAt: event.at })

    case 'job.worker.added':
      if (!state.workersById[event.worker.workerId]) return state
      return addJobWorker(state, event.jobId, event.worker)

    case 'job.worker.removed':
      return removeJobWorker(state, event.jobId, event.workerId)

    case 'job.worker.response':
      return patchJobWorker(state, event.jobId, event.workerId, {
        response: event.response,
        respondedAt: event.at,
        ...(event.declineReason !== undefined ? { declineReason: event.declineReason } : {}),
      })

    case 'job.worker.status':
      return patchJobWorker(state, event.jobId, event.workerId, { status: event.status })

    case 'job.worker.reminded':
      return patchJobWorker(state, event.jobId, event.workerId, { lastReminderAt: event.at })

    case 'job.required.changed': {
      const job = state.jobsById[event.jobId]
      if (!job || event.requiredWorkers < 1) return state
      return upsertJob(state, { ...job, requiredWorkers: Math.round(event.requiredWorkers) })
    }

    default:
      return state
  }
}

export function applyRealtimeEvents(state: EntitiesState, events: RealtimeEvent[]): EntitiesState {
  return events.reduce(applyRealtimeEvent, state)
}

export interface SnapshotMergeResult {
  state: EntitiesState
  newJobIds: string[]
  changedJobIds: string[]
}

/**
 * Reconciles a polled snapshot into existing state as a patch: unchanged entities keep their
 * references, removed entities are dropped, and new/changed job ids are reported for highlighting.
 */
export function mergeSnapshot(state: EntitiesState, snapshot: Pick<RealtimeSnapshot, 'workers' | 'jobs'>): SnapshotMergeResult {
  const incoming = hydrateEntities(snapshot)
  const newJobIds: string[] = []
  const changedJobIds: string[] = []

  const workersById: Record<string, Worker> = {}
  incoming.workerIds.forEach((id) => {
    const previous = state.workersById[id]
    const next = incoming.workersById[id]
    workersById[id] = previous && shallowEqual(previous, next) ? previous : next
  })

  const jobsById: Record<string, Job> = {}
  incoming.jobIds.forEach((id) => {
    const previous = state.jobsById[id]
    const next = incoming.jobsById[id]
    if (!previous) {
      newJobIds.push(id)
      jobsById[id] = next
    } else if (jobsEqual(previous, next)) {
      jobsById[id] = previous
    } else {
      changedJobIds.push(id)
      jobsById[id] = next
    }
  })

  const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id, index) => id === b[index])
  const workersUnchanged = sameIds(state.workerIds, incoming.workerIds)
    && incoming.workerIds.every((id) => workersById[id] === state.workersById[id])
  const jobsUnchanged = sameIds(state.jobIds, incoming.jobIds) && newJobIds.length === 0 && changedJobIds.length === 0

  return {
    state: workersUnchanged && jobsUnchanged
      ? state
      : {
          workersById: workersUnchanged ? state.workersById : workersById,
          workerIds: sameIds(state.workerIds, incoming.workerIds) ? state.workerIds : incoming.workerIds,
          jobsById: jobsUnchanged ? state.jobsById : jobsById,
          jobIds: sameIds(state.jobIds, incoming.jobIds) ? state.jobIds : incoming.jobIds,
        },
    newJobIds,
    changedJobIds,
  }
}

/** Restores a single job (used to roll back an optimistic assignment). */
export function restoreJob(state: EntitiesState, job: Job): EntitiesState {
  return upsertJob(state, job)
}

/**
 * Rolls back an optimistic change to one job's staffing (workers, required count, status)
 * while keeping anything else that changed meanwhile (e.g. a location or time update).
 */
export function rollbackJobStaffing(
  state: EntitiesState,
  previous: Pick<Job, 'id' | 'workers' | 'requiredWorkers' | 'status'>,
): EntitiesState {
  const current = state.jobsById[previous.id]
  if (!current) return state
  return upsertJob(state, { ...current, workers: previous.workers, requiredWorkers: previous.requiredWorkers, status: previous.status })
}
